import { useMemo } from 'react';

import styled from 'styled-components';

import { GetColumnsProductQuery, useGetColumnsProductQuery } from 'data/__generated__';

import { SectionContainer } from 'components/DetailsView/Section';
import { DataTable, numberColumn, textColumn } from 'components/DataTable';
import { Loader, LoaderModeType } from 'components/Loader';

type ColumnStockRow = GetColumnsProductQuery['columnsProduct'][number];

export const ColumnStocksSection = ({ productId }: { productId: string }) => {
    const { data, loading, error } = useGetColumnsProductQuery({
        variables: { productId },
        fetchPolicy: 'cache-and-network',
    });

    const columns = useMemo(
        () => [
            textColumn<ColumnStockRow, never>({
                accessorFn: (row) => row.column.fullname,
                id: 'columnFullname',
                header: 'Colonne',
                defaultVisible: true,
            }),
            textColumn<ColumnStockRow, never>({
                accessorFn: (row) => row.column.type,
                id: 'columnType',
                header: 'Type',
                defaultVisible: true,
            }),
            numberColumn<ColumnStockRow, never>({
                accessorKey: 'stock',
                header: 'Stock',
                defaultVisible: true,
            }),
        ],
        [],
    );

    return (
        <SectionContainer title="Stock par colonne" isInitiallyOpen={false}>
            <Container>
                {loading && !data ? (
                    <Loader mode={LoaderModeType.Spin} />
                ) : error ? (
                    <ErrorMessage>Erreur lors de la récupération du stock par colonne</ErrorMessage>
                ) : (
                    <TableContainer>
                        <DataTable<ColumnStockRow>
                            columns={columns}
                            data={data?.columnsProduct ?? []}
                            filterMode="client"
                            getRowId={(row) => row.columnId}
                            getRowLink={(row) => `/column/${row.columnId}/stocks/${productId}`}
                            defaultSortBy="stock"
                            defaultSortOrder="desc"
                            disableUrlSync
                            disableColumnVisibility
                            autoFocusSearch={false}
                            emptyMessage="Aucune colonne avec du stock pour ce produit"
                        />
                    </TableContainer>
                )}
            </Container>
        </SectionContainer>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const TableContainer = styled.div`
    height: 400px;
    overflow: hidden;
`;

const ErrorMessage = styled.div`
    color: ${({ theme }) => theme.errorColor};
    padding: 10px;
`;
